import React from 'react';
import { useT } from '../../context/LanguageContext';

/**
 * What the top of the kiosk shows once the screen has been lowered.
 *
 * The accessibility button drops every control into the bottom 60% of the
 * panel, within reach of somebody seated, and leaves the top as display only.
 * Display only used to mean one grey sentence and half a metre of blank glass,
 * which from a wheelchair looked like the panel had stopped working. So this
 * stands there instead: the same family as the attract screen's monster
 * (AdSlotMonster) and the Dine in / Take away pair (ServiceModeMonsters),
 * leaning over the orange line and pointing at the band below, where the
 * buttons now are. Its speech bubble is the sentence that was there before.
 *
 * Drawn, not downloaded — one SVG, coloured from the kiosk's own tokens, and
 * everything that moves is CSS (kiosk.css, `.k-lowered-*`), so it all stops
 * under prefers-reduced-motion. The bubble carries the words; the drawing is
 * described once for a screen reader and otherwise left alone. None of it
 * takes a tap: the point of the mode is that nothing up here needs one.
 */
export default function LoweredScreenMonster() {
  const { t } = useT();
  return (
    <div className="k-a11y-note">
      <svg className="k-lowered-art" viewBox="0 0 340 330" role="img"
        aria-label={t('kiosk.a11y.alt')}>
        {/* The ground he stands on, so he is not floating in space. */}
        <ellipse className="k-lowered-shadow" cx="150" cy="272" rx="78" ry="12" />

        <g className="k-lowered-body">
          {/* Antennae, with a bulb each — they lag behind the bob. */}
          <g className="k-lowered-antenna">
            <path d="M116 86 C108 56 94 46 86 38" />
            <circle cx="84" cy="34" r="11" />
          </g>
          <g className="k-lowered-antenna k-lowered-antenna-b">
            <path d="M184 86 C192 56 206 46 214 38" />
            <circle cx="216" cy="34" r="11" />
          </g>

          {/* The body: a soft blob, wider at the foot. */}
          <path className="k-lowered-skin"
            d="M150 66 C212 66 242 112 242 164 C242 220 206 252 150 252
               C94 252 58 220 58 164 C58 112 88 66 150 66 Z" />

          {/* The arm that is not pointing rests on the hip. */}
          <path className="k-lowered-limb" d="M64 162 C40 170 30 190 34 208" />

          {/* The pointing arm: shoulder to hand, then a finger. The group
              swings about the shoulder, so the finger prods the air just
              above the orange line rather than hanging still. */}
          <g className="k-lowered-point">
            <path className="k-lowered-limb" d="M236 170 C262 178 280 200 284 232" />
            <circle className="k-lowered-hand" cx="286" cy="240" r="15" />
            <path className="k-lowered-limb" d="M286 250 L286 266" />
          </g>

          {/* Eyes. The whites blink; the pupils follow the finger down and
              come up now and then to check you saw. Separate groups, so a
              blink never drags the pupil with it. */}
          <g className="k-lowered-blink">
            <ellipse className="k-lowered-eye" cx="118" cy="152" rx="30" ry="33" />
            <ellipse className="k-lowered-eye" cx="186" cy="152" rx="30" ry="33" />
            <g className="k-lowered-pupils">
              <circle className="k-lowered-pupil" cx="118" cy="154" r="13" />
              <circle className="k-lowered-pupil" cx="186" cy="154" r="13" />
              <circle className="k-lowered-glint" cx="123" cy="148" r="4.5" />
              <circle className="k-lowered-glint" cx="191" cy="148" r="4.5" />
            </g>
          </g>

          <circle className="k-lowered-cheek" cx="88" cy="190" r="12" />
          <circle className="k-lowered-cheek" cx="216" cy="190" r="12" />
          {/* A grin, open — a closed curve reads as a smirk at this size. */}
          <path className="k-lowered-mouth" d="M122 194 C136 214 168 214 182 194 Z" />

          <path className="k-lowered-limb" d="M116 252 L116 266" />
          <path className="k-lowered-limb" d="M184 252 L184 266" />
        </g>

        {/* Three chevrons under the finger, lighting up one after another
            towards the band. They stay put while the arm swings, which is
            what makes them read as a direction and not as part of the hand. */}
        <path className="k-lowered-chev" d="M270 280 L286 294 L302 280" />
        <path className="k-lowered-chev k-lowered-chev-b" d="M270 298 L286 312 L302 298" />
        <path className="k-lowered-chev k-lowered-chev-c" d="M270 316 L286 330 L302 316" />
      </svg>

      <div className="k-lowered-say">{t('kiosk.a11y.on')}</div>
    </div>
  );
}
